import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { fmtRM, fmtNum } from "../../lib/format";
import { useLang } from "../../lib/i18n";
import { StatCard, PageTitle, Spinner } from "../../components/Shell";
import { Users, Wallet, FileCheck2, HandCoins } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Overview() {
  const [data, setData] = useState(null);
  const { lang } = useLang();

  useEffect(() => {
    api.get("/tenant/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <Spinner />;

  return (
    <div data-testid="tenant-overview">
      <PageTitle title={lang === "bm" ? "Ringkasan Koperasi" : "Cooperative Overview"} sub={lang === "bm" ? "Prestasi semasa operasi koperasi anda" : "Current performance of your cooperative"} />
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard testid="stat-members" label={lang === "bm" ? "Ahli Aktif" : "Active Members"} value={`${fmtNum(data.members_active)} / ${fmtNum(data.members_total)}`} icon={Users} sub={`${data.kyc_pending} KYC pending`} />
        <StatCard testid="stat-shares" label={lang === "bm" ? "Modal Saham" : "Share Capital"} value={fmtRM(data.share_capital)} icon={Wallet} />
        <StatCard testid="stat-fees" label={lang === "bm" ? "Yuran Tertunggak" : "Fees Outstanding"} value={fmtRM(data.fees_outstanding)} icon={HandCoins} />
        <StatCard testid="stat-approvals" label={lang === "bm" ? "Kelulusan Pending" : "Pending Approvals"} value={fmtNum(data.pending_approvals)} icon={FileCheck2} />
      </div>

      <div className="grid lg:grid-cols-5 gap-6 mt-6">
        <div className="lg:col-span-3 bg-card rounded-xl border border-brand-deep/10 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-brand-dark">{lang === "bm" ? "Kutipan Bulanan" : "Monthly Collections"}</h3>
            <span className="text-xs font-semibold text-brand-deep bg-brand-light rounded-full px-3 py-1">
              {lang === "bm" ? "Bulan ini" : "This month"}: {fmtRM(data.collected_this_month)}
            </span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthly_collections}>
                <CartesianGrid strokeDasharray="3 3" stroke="#0F4C3A15" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmtRM(v)} />
                <Bar dataKey="total" fill="#0284C7" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="lg:col-span-2 bg-card rounded-xl border border-brand-deep/10 p-5">
          <h3 className="font-display font-semibold text-brand-dark mb-4">{lang === "bm" ? "Bayaran Terkini" : "Recent Payments"}</h3>
          <div className="space-y-3">
            {data.recent_payments.map((p) => (
              <div key={p.payment_id} className="flex items-center justify-between gap-3 text-sm border-b border-brand-deep/5 pb-3 last:border-0">
                <div className="min-w-0">
                  <div className="font-medium text-brand-dark truncate">{p.member_name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{p.receipt_no} • {p.method}</div>
                </div>
                <div className="font-mono font-semibold text-brand-deep shrink-0">{fmtRM(p.amount)}</div>
              </div>
            ))}
            {data.recent_payments.length === 0 && <p className="text-sm text-muted-foreground">Tiada bayaran lagi.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
